import * as geom from "js/core/utilities/geom";
import { AnimationSequenceType, FormatType, TextBlockPreset } from "common/constants.js";
import { TextElement } from "js/elements/base/TextElement";
import { SVGRectElement } from "js/elements/base/SVGElement";
import { VerticalAlignType } from "common/constants";
import { layoutHelper } from "js/elements/layouts/LayoutHelper";
import CompareValuesItem from "./CompareValuesItem";
import CompareValuesItemLabel from "./CompareValuesItemLabel";

export default class CompareHorizontalBarItem extends CompareValuesItem {
    get selectionPadding() {
        return { left: 20, right: 0, top: 0, bottom: 0 };
    }

    get labelText() {
        return this.getRootElement().formatValue(this.currentValue);
    }

    _build() {
        this.shape = this.addElement("shape", () => SVGRectElement);

        this.text = this.addElement("title", () => TextElement, {
            allowedBlockTypes: TextBlockPreset.DEFAULT,
            autoWidth: false,
            autoHeight: true
        });

        if (this.parentElement.format != FormatType.NONE) {
            this.label = this.addElement("label", () => CompareValuesItemLabel, {
                model: {
                    label: this.labelText
                },
                autoWidth: true,
                canEdit: false
            });
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        size = new geom.Size(size.width, options.barHeight);

        let textProps = children.setProps(this.text, new geom.Size(options.titleWidth, options.barHeight));
        textProps.bounds = new geom.Rect(0, size.height / 2 - textProps.size.height / 2, options.titleWidth, textProps.size.height);

        let compare = this.getRootElement();

        let availableWidth = size.width - this.text.bounds.width;
        let barWidth = availableWidth * this.percentageValue;
        if (this.isAnimating) {
            barWidth *= this.drawPercentage;
        }

        let shapeProps = children.setProps(this.shape);
        shapeProps.layer = -1;

        if (compare.minValue >= 0) {
            shapeProps.bounds = new geom.Rect(textProps.bounds.width, 0, barWidth, size.height);
        } else {
            shapeProps.bounds = new geom.Rect(0, 0, barWidth, size.height);
            let baseLineX = textProps.bounds.width + availableWidth - availableWidth * Math.abs(compare.maxValue) / (compare.maxValue - compare.minValue);
            if (this.model.value >= 0) {
                shapeProps.bounds.left = baseLineX;
            } else {
                shapeProps.bounds.left = baseLineX - barWidth;
            }
        }

        if (this.label) {
            this.label.styles.fontSize = size.height * 0.5;

            if (this.isAnimating) {
                this.label.updateText(this.labelText);
            }

            let labelProps = children.setProps(this.label, size);

            const labelOutsideAndIsAnimating = this.label.isInside === false && this.isAnimating;
            if (!labelOutsideAndIsAnimating && shapeProps.bounds.width > labelProps.size.width) {
                if (!this.isAnimating) {
                    this.label.isInside = true;
                }
                labelProps.bounds = new geom.Rect(this.shape.bounds.right - labelProps.size.width, shapeProps.bounds.top + layoutHelper.getVerticalAlignOffset(labelProps.size.height, shapeProps.bounds.height, VerticalAlignType.MIDDLE), labelProps.size);
            } else {
                if (!this.isAnimating) {
                    this.label.isInside = false;
                }
                labelProps.bounds = new geom.Rect(shapeProps.bounds.right, shapeProps.bounds.top + layoutHelper.getVerticalAlignOffset(labelProps.size.height, shapeProps.bounds.height, VerticalAlignType.MIDDLE), labelProps.size);
            }
        }

        return { size };
    }

    getBackgroundColor(forElement) {
        if (this.label && forElement === this.label && this.label.isInside) {
            return this.getShapeFillColor(this.shape);
        } else {
            return super.getBackgroundColor(forElement);
        }
    }

    get buildAnimations() {
        return [{
            target: this.DOMNode,
            type: AnimationSequenceType.FADE,
            duration: 400
        }, {
            type: AnimationSequenceType.ANIMATE_TO_VALUE,
            target: this,
            prepare: () => {
                this.animationState = { value: 0 };
            },
            callback: value => {
                this.animationState = { value };
            },
            reset: () => {
                this.animationState = { value: 1 };
            },
            startValue: 0,
            endValue: 1,
        }, {
            type: AnimationSequenceType.FADE,
            target: this.text.DOMNode,
            delay: duration => duration - 400
        }];
    }
}
